import FileTable from '@/components/FileTable';
import { useCachedFile } from '@/hooks/useCachedFile';
import { useCachedFileData } from '@/hooks/useCachedFileData';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { FC, useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import PDFViewer from '@/components/PDFViewer';
import ProcessingStatus from '@/components/ProcessingStatus';
import { clearCanvas, getCurrentPageData } from '@/globals/global';
import CopyPasteComponent from '@/components/CopyPasteComponent';
import ExportButtons from '@/components/ExportButtons';
import RenameFileButton from '@/components/RenameFileButton';
import AddSwitchboard from '@/components/AddSwitchboard';
import LastUpdateInfo from '@/components/LastUpdateInfo';
import UndoRedoButtons from '@/components/UndoRedoButtons';
import { toast } from 'react-toastify';
import api from '@/api/api.ts';

export const FilePage: FC = () => {
  const { fileId } = Route.useParams();
  const navigate = useNavigate();
  const pdfWidth = 1120;
  const pdfHeight = 806;
  const [isInitialStatusSet, setIsInitialStatusSet] = useState(false);
  const search = useSearch({ from: `/file/$fileId/` }) as { page?: string };
  const initialPageNumber = search.page ? parseInt(search.page, 10) : 1;
  const [pageNumber, setPageNumber] = useState(1);
  const [highlightedPinId, setHighlightedPinId] = useState<string | null>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [currentDrawingPin, setCurrentDrawingPin] = useState<string | null>(null);

  const {
    file,
    isLoading: fileLoading,
    isCacheChecked: isCacheFileChecked,
    compareAndFetchFile,
    error: fileError,
  } = useCachedFile(fileId, pageNumber - 1);
  const { fileData, isLoading: fileDataLoading, compareAndFetch, refetch } = useCachedFileData(fileId, pageNumber - 1);

  /**
   * Ensure page number is set correctly from URL on initial load
   */
  useEffect(() => {
    if (search.page) {
      const pageFromUrl = parseInt(search.page, 10);
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      if (!isNaN(pageFromUrl) && pageFromUrl > 0) {
        setPageNumber(pageFromUrl);
      }
    }
  }, [search.page]);

  /**
   * Initial fetch of file and metadata
   */
  useEffect(() => {
    const checkAndUpdateStatus = async () => {
      if (!isInitialStatusSet && !fileDataLoading) {
        await compareAndFetch();
        if (fileData) {
          setIsInitialStatusSet(true);
        }
      }
    };

    checkAndUpdateStatus();
  }, [isInitialStatusSet, fileDataLoading, compareAndFetch, fileData]);

  /**
   * Trigger updates when file data changes
   */
  useEffect(() => {
    if (isCacheFileChecked && !file && !fileLoading) {
      compareAndFetchFile();
    }
  }, [isCacheFileChecked, file, fileLoading, compareAndFetchFile]);

  /**
   * When file has error, return to error page
   */
  useEffect(() => {
    if (
      fileError &&
      typeof fileError === 'object' &&
      'response' in fileError &&
      (fileError as AxiosError).response?.status === 404
    ) {
      navigate({ to: '/error' });
    }
  }, [fileError, navigate]);

  /**
   * Refetch data when a page change occurs
   */
  const handlePageChange = useCallback(
    async (newPage: number) => {
      setPageNumber(newPage);
      await refetch();
    },
    [refetch]
  );

  /**
   * Send a request to add bounding_box to the server when drawing is complete
   */
  const handleDrawingComplete = useCallback(
    async (
      boundingBox: {
        leftmost: number;
        rightmost: number;
        topmost: number;
        bottommost: number;
      },
      pinUid: string | null
    ) => {
      if (!pinUid) {
        return;
      }

      try {
        await api.put(`pin/${pinUid}`, { bounding_box: boundingBox });
        setIsDrawingMode(false);
        setCurrentDrawingPin(null);
        await refetch();

        toast.success('Přidán bounding_box pro daný řádek.', {
          autoClose: 1000,
          position: 'top-right',
        });
      } catch (error: any) {
        console.error('Error saving drawing:', error);
        toast.error('Chyba při přidávání bounding_box pole.', {
          autoClose: 3000,
          position: 'top-right',
        });
      }
    },
    [setIsDrawingMode, setCurrentDrawingPin]
  );

  const handleDrawingEnd = useCallback(() => {
    // This will trigger the onDrawingComplete callback in PDFViewer
  }, []);

  return (
    <>
      {fileData && <ProcessingStatus fileId={fileId} />}
      <div className={'flex justify-between items-center gap-2 w-full mt-8'}>
        {fileData?.filename && (
          <h1 className={'text-xl flex items-center gap-2'}>
            Soubor: <span className={'font-bold'}>{fileData?.filename}</span>
            <RenameFileButton
              fileId={fileId}
              currentFileName={fileData?.filename || ''}
              onRename={async () => {
                await refetch();
                setPageNumber((prevPageNumber) => prevPageNumber);
              }}
              isDisabled={isDrawingMode}
            />
          </h1>
        )}
        {fileData && (
          <div className={'flex items-center gap-2'}>
            <ExportButtons fileId={fileId} isDisabled={isDrawingMode} />
            <span className={'w-0 h-8 border-l border-gray-200 mx-1'} />
            <CopyPasteComponent
              fileId={fileId}
              pageNumber={pageNumber}
              refetch={async () => {
                await refetch();
              }}
              clearCanvas={() => clearCanvas}
              isDisabled={isDrawingMode}
            />
          </div>
        )}
      </div>
      <div className={'flex flex-wrap items-start gap-4 justify-center w-full mt-8'}>
        <PDFViewer
          file={file}
          fileData={fileData}
          pdfWidth={pdfWidth}
          pdfHeight={pdfHeight}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          isDrawingMode={isDrawingMode}
          highlightedPinId={highlightedPinId}
          fileId={fileId}
          onPageChange={handlePageChange}
          initialPageNumber={initialPageNumber}
          navigate={navigate}
          onDrawingComplete={handleDrawingComplete}
          currentDrawingPin={currentDrawingPin}
        />

        <div className={'relative'}>
          <div className={'flex items-stretch justify-between gap-2 mb-1'}>
            <LastUpdateInfo fileData={fileData} pageNumber={pageNumber - 1} />
            <AddSwitchboard
              fileId={fileId}
              pageNumber={pageNumber}
              onAdd={async () => {
                await refetch();
                setPageNumber((prevPageNumber) => prevPageNumber);
              }}
              isDisabled={isDrawingMode}
            />
          </div>
          {fileData && (fileData.page_data.exists_previous_version || fileData.page_data.exists_next_version) && (
            <UndoRedoButtons
              fileUid={fileData.page_data.file_uid}
              pageNumber={pageNumber}
              existsPreviousVersion={fileData.page_data.exists_previous_version}
              existsNextVersion={fileData.page_data.exists_next_version}
              refetch={async () => {
                await refetch();
              }}
              isDisabled={isDrawingMode}
            />
          )}
          <FileTable
            fileData={getCurrentPageData(fileData, pageNumber)}
            fileId={fileId}
            page={pageNumber - 1}
            key={`file-table-${pageNumber}-${fileData?.page_data.updated_at}`}
            setHighlightedPinId={setHighlightedPinId}
            setIsDrawingMode={setIsDrawingMode}
            setCurrentDrawingPin={setCurrentDrawingPin}
            onDrawingEnd={handleDrawingEnd}
          />
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute('/file/$fileId/')({
  component: FilePage,
});

export default FilePage;
